/**
 * classifyTabQuery — pure parse + render-mode dispatcher for the Visual view
 * (`mode/QueryVisualModePanel`), the compact popover and the inline toolbar.
 *
 * Accepts an optional `cursorOffset` so callers (the inline toolbar in
 * particular) can ask "what statement is the cursor in?" instead of always
 * picking statements[0]. Falls back to statements[0] when the cursor is
 * outside any statement (e.g., whitespace between statements).
 */
import { useMemo } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../../sql-grammar/document';
import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import { detectRenderMode } from '../detection/detectRenderMode';
import { databaseEngineIdToSqlEngine } from '../edit/livePullSchemaSnapshot';
import type { RenderMode } from '../types';

/** Render modes shown as the read-only fallback card. */
export const FALLBACK_REASONS: ReadonlySet<RenderMode> = new Set<RenderMode>([
  'unsupported-ddl',
  'unsupported-procedural',
  'unsupported-set-precedence',
  'unsupported-graph-table',
  'unsupported-unknown',
]);

export type ClassificationResult = {
  buffer: ParsedBuffer;
  statement: StatementInfo | null;
  renderMode: RenderMode;
};

export function classifyTabQuery(
  query: string,
  options: ParseOptions,
  cursorOffset?: number,
): ClassificationResult {
  const parsedDocument = buildParsedDocumentFromSource(query, options);
  const documentStructure = getDocumentStructure(parsedDocument, 'visual-query-tab');
  const buffer: ParsedBuffer = {
    parsedDocument,
    documentStructure,
  };
  const cursorStatement = typeof cursorOffset === 'number'
    ? documentStructure.statementAt(cursorOffset)
    : null;
  const statement = cursorStatement ?? documentStructure.statements[0] ?? null;
  if (!statement) {
    return { buffer, statement: null, renderMode: 'parse-error' };
  }
  const cursor = { offset: statement.range.startOffset, line: 0, column: 0 };
  const renderMode = detectRenderMode(buffer, cursor);
  return { buffer, statement, renderMode };
}

// `compoundBlockAware` + `treatBareBeginAsRoutineBody` make the VQE's OWN
// statement classification (and therefore the statement picker's count) treat
// a CREATE/ALTER PROCEDURE|FUNCTION|TRIGGER|EVENT body, an Oracle anonymous
// PL/SQL block, and a bare mssql/mysql/sqlite `BEGIN … END` as ONE statement
// instead of splitting at every inner `;` (EDG-17/EDG-19). Deliberately local
// to the VQE: query *execution* splitting builds its own `{ engine }` options,
// so live batching is unchanged by either flag.
const VQE_PARSE_FLAGS = { compoundBlockAware: true, treatBareBeginAsRoutineBody: true } as const;

/** The VQE's parse options for a connection engine (postgres when unknown). */
export function vqeParseOptionsFor(engineId: DatabaseEngineId | undefined): ParseOptions {
  const engine = engineId ? databaseEngineIdToSqlEngine(engineId) : null;
  return { engine: engine ?? 'postgres', ...VQE_PARSE_FLAGS };
}

/** Memoised `classifyTabQuery`; `null` for a blank buffer (the starter canvas). */
export function useVqeClassification(
  query: string,
  options: ParseOptions,
  statementOffset: number | null | undefined,
): ClassificationResult | null {
  return useMemo(
    () => (query.trim().length === 0 ? null : classifyTabQuery(query, options, statementOffset ?? undefined)),
    [query, options, statementOffset],
  );
}
